import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const ExpenseContext = createContext(null);

export const ExpenseProvider = ({ children }) => {
  const [expenseFilters, setExpenseFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const [expenseResults, setExpenseResults] = useState([]);
  const [filteredExpenseResults, setFilteredExpenseResults] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseCurrentPage, setExpenseCurrentPage] = useState(1);
  const [expenseSortConfig, setExpenseSortConfig] = useState({
    key: 'date',
    direction: 'descending',
  });
  const [expenseSearchInputs, setExpenseSearchInputs] = useState({});

  // FIX #4: treat empty string as null so it doesn't falsely match
  const getExpenseId = (expense) => {
    const id = expense?.expenseId ?? expense?.id ?? expense?.docId ?? expense?.sheetId;
    return id != null && id !== '' ? id : null;
  };

  // FIX #1: wrap helpers in useCallback so useMemo deps are stable
  const updateExpenseInCache = useCallback((updatedExpense) => {
    const updatedId = getExpenseId(updatedExpense);
    if (updatedId == null) return;

    setExpenseResults((prev) =>
      prev.map((item) =>
        getExpenseId(item) === updatedId ? { ...item, ...updatedExpense } : item
      )
    );

    setFilteredExpenseResults((prev) => {
      if (!prev) return prev;
      return prev.map((item) =>
        getExpenseId(item) === updatedId ? { ...item, ...updatedExpense } : item
      );
    });

    setSelectedExpense((prev) => {
      if (!prev) return prev;
      return getExpenseId(prev) === updatedId
        ? { ...prev, ...updatedExpense }
        : prev;
    });
  }, []);

  const addExpenseToCache = useCallback((newExpense) => {
    if (!newExpense) return;

    setExpenseResults((prev) => [...prev, newExpense]);

    setFilteredExpenseResults((prev) => {
      if (!prev) return prev;
      return [...prev, newExpense];
    });

    setSelectedExpense(newExpense);
  }, []);

  const replaceExpenseCache = useCallback((newList) => {
    setExpenseResults(Array.isArray(newList) ? newList : []);
    setFilteredExpenseResults(null);
    setExpenseCurrentPage(1);
  }, []);

  // FIX #1: helpers added to deps array
  const value = useMemo(
    () => ({
      expenseFilters,
      setExpenseFilters,
      expenseResults,
      setExpenseResults,
      filteredExpenseResults,
      setFilteredExpenseResults,
      selectedExpense,
      setSelectedExpense,
      expenseCurrentPage,
      setExpenseCurrentPage,
      expenseSortConfig,
      setExpenseSortConfig,
      expenseSearchInputs,
      setExpenseSearchInputs,
      updateExpenseInCache,
      addExpenseToCache,
      replaceExpenseCache,
    }),
    [
      expenseFilters,
      expenseResults,
      filteredExpenseResults,
      selectedExpense,
      expenseCurrentPage,
      expenseSortConfig,
      expenseSearchInputs,
      updateExpenseInCache,
      addExpenseToCache,
      replaceExpenseCache,
    ]
  );

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  );
};

export const useExpenseContext = () => {
  const ctx = useContext(ExpenseContext);
  if (!ctx) {
    throw new Error('useExpenseContext must be used inside ExpenseProvider');
  }
  return ctx;
};
